import React, { useEffect, useState, useRef } from 'react';
import { Building2, Star, Users, TrendingUp } from 'lucide-react';

const StatsCounter = ({ businesses = 0, reviews = 0, users = 0 }) => {
    const [counts, setCounts] = useState({ businesses: 0, reviews: 0, users: 0 });
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!isVisible) return;

        const duration = 2000; // 2 seconds
        const steps = 60;
        const stepDuration = duration / steps;

        const businessesIncrement = businesses / steps;
        const reviewsIncrement = reviews / steps;
        const usersIncrement = users / steps;

        let currentStep = 0;

        const timer = setInterval(() => {
            currentStep++;

            if (currentStep <= steps) {
                setCounts({
                    businesses: Math.floor(businessesIncrement * currentStep),
                    reviews: Math.floor(reviewsIncrement * currentStep),
                    users: Math.floor(usersIncrement * currentStep),
                });
            } else {
                setCounts({ businesses, reviews, users });
                clearInterval(timer);
            }
        }, stepDuration);

        return () => clearInterval(timer);
    }, [isVisible, businesses, reviews, users]);

    const stats = [
        {
            icon: Building2,
            label: 'Businesses',
            value: counts.businesses,
            suffix: '+',
            color: 'from-blue-500 to-blue-600',
            bgColor: 'bg-blue-50 dark:bg-blue-900/20',
            iconColor: 'text-blue-600 dark:text-blue-400',
        },
        {
            icon: Star,
            label: 'Reviews',
            value: counts.reviews,
            suffix: '+',
            color: 'from-yellow-500 to-yellow-600',
            bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
            iconColor: 'text-yellow-600 dark:text-yellow-400',
        },
        {
            icon: Users,
            label: 'Happy Users',
            value: counts.users,
            suffix: '+',
            color: 'from-green-500 to-green-600',
            bgColor: 'bg-green-50 dark:bg-green-900/20',
            iconColor: 'text-green-600 dark:text-green-400',
        },
        {
            icon: TrendingUp,
            label: 'Trusted Platform',
            value: '100',
            suffix: '%',
            color: 'from-purple-500 to-purple-600',
            bgColor: 'bg-purple-50 dark:bg-purple-900/20',
            iconColor: 'text-purple-600 dark:text-purple-400',
            noAnimation: true,
        },
    ];

    return (
        <div ref={sectionRef} className="py-12 md:py-16 bg-gray-50 dark:bg-gray-900/50">
            <div className="container-custom">
                <div className="text-center mb-10">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-50 mb-3">
                        Trusted by the Community
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                        Join thousands of locals discovering and supporting businesses in Fatehabad
                    </p>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                    {stats.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <div
                                key={index}
                                className="group relative bg-white dark:bg-gray-800 rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                {/* Icon */}
                                <div className={`${stat.bgColor} w-14 h-14 md:w-16 md:h-16 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                                    <Icon className={`h-7 w-7 md:h-8 md:w-8 ${stat.iconColor}`} />
                                </div>

                                {/* Value */}
                                <div className="mb-2">
                                    <span className={`text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                                        {stat.noAnimation ? stat.value : stat.value.toLocaleString()}
                                    </span>
                                    <span className={`text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                                        {stat.suffix}
                                    </span>
                                </div>

                                {/* Label */}
                                <p className="text-gray-600 dark:text-gray-400 font-medium text-sm md:text-base">
                                    {stat.label}
                                </p>

                                {/* Decorative gradient */}
                                <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${stat.color} opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-opacity duration-300`}></div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default StatsCounter;
